import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import forgotPassword from "@insite/client-framework/Store/Context/Handlers/ForgotPassword";
import translate from "@insite/client-framework/Translate";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import React, { RefObject, useContext, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    accountSettings: getSettingsCollection(state).accountSettings,
});

interface OwnProps {
    onClose: () => void;
}

const mapDispatchToProps = {
    resetPassword: forgotPassword,
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: React.RefObject<ReCAPTCHA | null> }, boolean>(
        validateReCaptcha,
    ),
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface SignInResetPasswordFormCheckoutStyles {
    container?: GridContainerProps;
    descriptionGridItem?: GridItemProps;
    descriptionText?: TypographyPresentationProps;
    userNameGridItem?: GridItemProps;
    successGridItem?: GridItemProps;
    userNameTextField?: TextFieldProps;
    reCaptchaGridItem?: GridItemProps;
    buttonsGridItem?: GridItemProps;
    cancelButton?: ButtonPresentationProps;
    sendButton?: ButtonPresentationProps;
}

export const SignInResetPasswordFormCheckoutStyles: SignInResetPasswordFormCheckoutStyles = {
    userNameGridItem: {
        width: 12,
    },
    successGridItem: {
        width: 12,
        css: css`
            padding: 0px 0px 0px 18px !important;
            span {
                margin-top: -5px !important;
                font-weight: 600;
                font-size: 15px;
            }
        `,
    },
    descriptionGridItem: {
        width: 12,
    },
    reCaptchaGridItem: {
        width: 12,
    },
    buttonsGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    cancelButton: {
        mergeCss: true,
        color: "secondary",
        css: css`
            margin-right: 10px;
        `,
    },
};

/**
 * @deprecated Use SignInResetPasswordFormCheckoutStyles instead.
 */
// export const createListFormStyles = SignInResetPasswordFormCheckoutStyles;
const styles = SignInResetPasswordFormCheckoutStyles;

const SignInResetPasswordFormCheckoutCheckout = ({
    onClose,
    resetPassword,
    validateReCaptcha,
    accountSettings,
}: Props) => {
    const [userName, setUserName] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const toasterContext = useContext(ToasterContext);
    const { useEmailAsUserName } = accountSettings;
    const recaptchaRef: React.RefObject<ReCAPTCHA | null> = React.createRef();

    const [isEmailSent, setIsEmailSent] = useState(false);
    const [isEmailSend, setIsEmailSend] = useState(false);

    const sendEmail = async (userName: string) => {
        if (!userName.trim()) {
            setErrorMessage(
                siteMessage(
                    "Field_Required",
                    useEmailAsUserName ? translate("Email") : translate("Username"),
                ) as string,
            );
            return;
        }
        if (useEmailAsUserName && !validateEmail(userName)) {
            setErrorMessage(siteMessage("ResetPassword_EmailAddress_Validation") as string);
            return;
        }

        const isReCaptchaValid = await validateReCaptcha({
            location: "ForgotPassword",
            recaptchaRef,
        });
        if (!isReCaptchaValid) {
            return;
        }

        setIsEmailSend(true);
        setIsSubmitting(true);
        resetPassword({
            userName,
            onSuccess: () => {
                setIsEmailSent(true);
                setIsEmailSend(false);
                resetForm();
            },
            onError: error => {
                setErrorMessage(error);
                setIsEmailSend(false);
            },
            onComplete(resultProps) {
                setIsSubmitting(false);
                if (resultProps.apiResult?.successful) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                } else if (resultProps.apiResult?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(resultProps.apiResult.errorMessage);
                }
                setIsEmailSend(false);
            },
        });
    };

    const onCancelClick = () => {
        onClose();
        resetForm();
    };

    const resetForm = () => {
        setUserName("");
    };

    const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newUsername = e.currentTarget.value;
        setUserName(newUsername);
        setErrorMessage(
            newUsername
                ? ""
                : (siteMessage(
                      "Field_Required",
                      useEmailAsUserName ? translate("Email") : translate("Username"),
                  ) as string),
        );
    };

    return (
        <LoadingOverlay
            loading={isEmailSend}
            css={css`
                width: 100%;
            `}
        >
            <GridContainer {...styles.container}>
                <GridItem {...styles.descriptionGridItem}>
                    <Typography {...styles.descriptionText}>
                        {useEmailAsUserName
                            ? siteMessage("ResetPassword_EmailInstructions")
                            : siteMessage("ResetPassword_Instructions")}
                    </Typography>
                </GridItem>
                <GridItem {...styles.userNameGridItem}>
                    <TextField
                        {...styles.userNameTextField}
                        label={useEmailAsUserName ? translate("Email") : translate("Username")}
                        name="userName"
                        value={userName}
                        disabled={isEmailSent}
                        placeholder={useEmailAsUserName ? translate("Enter email") : translate("Enter username")}
                        error={errorMessage}
                        onChange={handleUsernameChange}
                        data-test-selector="resetPassword_userName"
                    />
                </GridItem>
                <GridItem {...styles.successGridItem}>
                    {isEmailSent && (
                        <Typography
                            css={css`
                                margin-top: 4px;
                                font-size: 14px;
                                color: ${getColor("primary")};
                            `}
                        >
                            {translate(
                                "If an account matches the email entered, an email will be sent to the associated email address with instructions on how to reset your password shortly. If you do not receive an email, please contact customer service.",
                            )}
                        </Typography>
                    )}
                </GridItem>

                <GridItem {...styles.reCaptchaGridItem}>
                    <ReCaptcha recaptchaRef={recaptchaRef} location="ForgotPassword" />
                </GridItem>
                <GridItem {...styles.buttonsGridItem}>
                    <Button {...styles.cancelButton} onClick={onCancelClick}>
                        {translate("Return to sign in")}
                    </Button>
                    <Button
                        {...styles.sendButton}
                        disabled={isSubmitting}
                        onClick={() => sendEmail(userName)}
                        data-test-selector="resetPassword_submit"
                    >
                        {translate("Send Email")}
                    </Button>
                </GridItem>
            </GridContainer>
        </LoadingOverlay>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(SignInResetPasswordFormCheckoutCheckout);
